import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  fitStateFamily,
  MAX_ZOOM,
  MIN_ZOOM,
  scaleSelectorFamily,
} from '@/features/comparer';
import { useTracker } from '@/core/tracking';

import minusIcon from '@/public/images/icons/minus.svg';
import plusIcon from '@/public/images/icons/plus.svg';
import expandIcon from '@/public/images/icons/expand.svg';
import React, { MouseEventHandler } from 'react';
import {
  ConvertedImage,
  defaultOutputForConversion,
} from '@/services/uploaderService';
import { mediaShownInComparerState } from '@/stores/mediaStore';

export default function ScaleRangeSlider({
  className = '',
}: {
  className?: string;
}) {
  const image = useRecoilValue(mediaShownInComparerState);

  const mediaInfo: {
    inputUrl?: string;
    outputUrl?: string;
    convertedImages?: ConvertedImage[];
  } = image;

  const { inputUrl } = mediaInfo;
  const outputUrl =
    mediaInfo.convertedImages?.find(
      (img) => img.imageContentType === defaultOutputForConversion
    )?.outputUri || mediaInfo.outputUrl;

  const setFit = useSetRecoilState(fitStateFamily(`${inputUrl}:${outputUrl}`));
  const [scale, setScale] = useRecoilState(
    scaleSelectorFamily(`${inputUrl}:${outputUrl}`)
  );

  const { track } = useTracker();

  // Do not settle for the default values
  if (!inputUrl || !outputUrl) {
    return (
      <ControlledScaleRangeSlider min={MIN_ZOOM} max={MAX_ZOOM} value={1} />
    );
  }

  function getPreviousWholeMultiple(n, base) {
    const previousWholeMultiple = n - (n % base);
    return previousWholeMultiple === n
      ? previousWholeMultiple - base
      : previousWholeMultiple;
  }
  function getNextWholeMultiple(n, base) {
    const previousWholeMultiple = getPreviousWholeMultiple(n, base);
    const nextWholeMultiple = previousWholeMultiple + base;
    return nextWholeMultiple === n
      ? nextWholeMultiple + base
      : nextWholeMultiple;
  }

  const percentageScale = Math.floor(scale * 100);

  return (
    <ControlledScaleRangeSlider
      className={className}
      min={MIN_ZOOM}
      max={MAX_ZOOM}
      value={scale}
      onDecrease={() => {
        setFit('none');
        setScale(getPreviousWholeMultiple(percentageScale, 25) / 100);
      }}
      onChange={(value) => {
        setFit('none');
        setScale(value);
      }}
      onIncrease={() => {
        track('editor', 'zoomed');
        setFit('none');
        setScale(getNextWholeMultiple(percentageScale, 25) / 100);
      }}
      onFitToScreen={() => {
        track('editor', 'fitted');
        setFit('contain');
      }}
    />
  );
}

function ControlledScaleRangeSlider({
  className = '',
  min,
  max,
  value,
  onChange,
  onIncrease,
  onDecrease,
  onFitToScreen,
}: {
  className?: string;
  min: number;
  max: number;
  value: any;
  onChange?: (e) => void;
  onIncrease?: MouseEventHandler;
  onDecrease?: MouseEventHandler;
  onFitToScreen?: MouseEventHandler;
}) {
  const percentageValue = Math.floor(value * 100);
  return (
    <div className={`flex divide-x divide-gray-500 ${className}`}>
      {/* Current scale */}
      <div className="flex items-center pe-4 lg:pe-6">
        <p className="tabular-nums M18">
          {percentageValue < 100 ? (
            <span className="invisible" aria-hidden="true">
              0
            </span>
          ) : null}
          {percentageValue}%
        </p>
      </div>

      {/* Slider */}
      <div className="flex items-center md:space-s-4 px-4 lg:px-6">
        <button className="hidden md:block" type="button" onClick={onDecrease}>
          <img {...minusIcon} alt="Decrease zoom" />
        </button>
        <input
          style={
            {
              '--active-color': 'white',
              '--inactive-color': 'rgba(255, 255, 255, 0.2)',
              '--progress': `${((value - min) / (max - min)) * 100}%`,
            } as React.CSSProperties
          }
          className="range"
          type="range"
          min={min}
          max={max}
          value={value}
          step={0.01}
          onChange={(e) => {
            if (typeof onChange === 'function') {
              onChange(e.currentTarget.value);
            }
          }}
        />
        <button className="hidden md:block" type="button" onClick={onIncrease}>
          <img {...plusIcon} alt="Increase zoom" />
        </button>
      </div>

      {/* Fit to screen */}
      <div className="flex items-center ps-2 lg:ps-6">
        <button type="button" onClick={onFitToScreen}>
          <img {...expandIcon} alt="Fit to screen" />
        </button>
      </div>
    </div>
  );
}
